export const SUPPORTED_APP_LANGUAGES = ["en", "de"] as const;
export type AppLanguage = (typeof SUPPORTED_APP_LANGUAGES)[number];

export const DEFAULT_APP_LANGUAGE: AppLanguage = "de";

/** Maps i18next / browser codes (e.g. en-US) to a supported app language. */
export function normalizeAppLanguage(lng?: string | null): AppLanguage {
  const base = (lng ?? "").split("-")[0].toLowerCase();
  return base === "de" ? "de" : "en";
}

export function getInitialAppLanguage(): AppLanguage {
  try {
    const saved = localStorage.getItem("i18nextLng");
    if (saved) {
      return normalizeAppLanguage(saved);
    }
  } catch (error) {
    console.warn("Failed to read language from localStorage:", error);
  }

  return normalizeAppLanguage(navigator.language);
}

export function getPrivacyPolicyHref(language?: string | null): string {
  return normalizeAppLanguage(language) === "de"
    ? "https://www.langguth.com/datenschutz/"
    : "https://www.langguth.com/en/privacy-policy/";
}

export function getImpressumHref(language?: string | null): string {
  return normalizeAppLanguage(language) === "de"
    ? "https://www.langguth.com/impressum/"
    : "https://www.langguth.com/en/imprint/";
}

export function toIntlLocale(language?: string | null): string {
  return normalizeAppLanguage(language) === "de" ? "de-DE" : "en-US";
}

/** Picks DE/EN API fields with sensible fallback when one locale is missing. */
export function localizedItemName(
  language: string | undefined | null,
  nameDe?: string | null,
  nameEn?: string | null,
): string {
  const lang = normalizeAppLanguage(language);
  const primary = lang === "de" ? nameDe : nameEn;
  const fallback = lang === "de" ? nameEn : nameDe;
  return (primary || fallback || "").trim();
}

export function syncDocumentLanguage(language: string): void {
  document.documentElement.lang = normalizeAppLanguage(language);
}

export function parseLangSearchParam(
  search: Record<string, unknown>,
): AppLanguage | undefined {
  return typeof search.lang === "string"
    ? normalizeAppLanguage(search.lang)
    : undefined;
}

/** Applies ?lang= from email auth links to i18n and document lang. */
export function applyLanguageFromSearch(
  i18n: { language: string; changeLanguage: (lng: string) => Promise<unknown> },
  lang: AppLanguage | undefined,
): void {
  if (!lang) return;
  if (i18n.language !== lang) {
    void i18n.changeLanguage(lang);
  }
  syncDocumentLanguage(lang);
}
